import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { API, CONTACT } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const DETAILS = [
    { icon: Phone, label: "Call us", value: CONTACT.phoneDisplay, href: CONTACT.phoneHref, testid: "contact-detail-phone" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-detail-email" },
    { icon: MapPin, label: "Service Area", value: "Pan-India · office address shared on request", testid: "contact-detail-address" },
    { icon: Clock, label: "Hours", value: CONTACT.hours, testid: "contact-detail-hours" },
];

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, { ...form, type: "contact" });
            toast.success("Message sent — we will reply within one working day.");
            setSent(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Contact"
                title="Let's plan something wonderful"
                description="Questions, custom requests or a date in mind — write to us, and a travel designer will reply personally."
            />
            <section className="py-20 lg:py-28">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-8">
                    <Reveal className="lg:col-span-3">
                        <div className="card-frame p-8 sm:p-10" data-testid="contact-form-card">
                            <h2 className="font-serif text-2xl font-semibold text-navy">Send a message</h2>
                            {sent ? (
                                <div className="mt-8 rounded-lg border border-brass/40 bg-brass-light/30 p-6 text-sm text-navy" data-testid="contact-success-note">
                                    Thank you — your message has reached us. We reply within one working day,
                                    usually much sooner.
                                </div>
                            ) : (
                                <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="contact-form">
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <Label htmlFor="ct-name" className="text-xs font-semibold uppercase tracking-wider text-steel">Full name</Label>
                                            <Input id="ct-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className="input-classic mt-2" data-testid="contact-name-input" />
                                        </div>
                                        <div>
                                            <Label htmlFor="ct-email" className="text-xs font-semibold uppercase tracking-wider text-steel">Email</Label>
                                            <Input id="ct-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="input-classic mt-2" data-testid="contact-email-input" />
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="ct-phone" className="text-xs font-semibold uppercase tracking-wider text-steel">Phone (optional)</Label>
                                        <Input id="ct-phone" value={form.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" className="input-classic mt-2" data-testid="contact-phone-input" />
                                    </div>
                                    <div>
                                        <Label htmlFor="ct-message" className="text-xs font-semibold uppercase tracking-wider text-steel">Message</Label>
                                        <Textarea id="ct-message" required minLength={2} rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className="input-classic mt-2 resize-none" data-testid="contact-message-input" />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="contact-submit-button"
                                        className="rounded-full bg-navy py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-navy-800 disabled:opacity-60"
                                    >
                                        {loading ? "Sending…" : (
                                            <span className="inline-flex items-center gap-2">
                                                Send Message <Send className="h-4 w-4 text-brass" />
                                            </span>
                                        )}
                                    </Button>
                                </form>
                            )}
                        </div>
                    </Reveal>

                    <div className="lg:col-span-2">
                        <Reveal delay={0.15}>
                            <h2 className="font-serif text-2xl font-semibold text-navy">Reach us directly</h2>
                            <div className="gold-rule mt-5" />
                            <ul className="mt-8 space-y-6">
                                {DETAILS.map((d) => (
                                    <li key={d.label} className="flex items-start gap-4" data-testid={d.testid}>
                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brass/40 bg-brass-light/40 text-brass-dark">
                                            <d.icon className="h-4 w-4" />
                                        </span>
                                        <span>
                                            <span className="block text-xs font-semibold uppercase tracking-wider text-steel">{d.label}</span>
                                            {d.href ? (
                                                <a href={d.href} className="mt-1 block text-sm font-medium text-navy transition-colors hover:text-brass-dark">
                                                    {d.value}
                                                </a>
                                            ) : (
                                                <span className="mt-1 block text-sm font-medium text-navy">{d.value}</span>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <a
                                href={CONTACT.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp-button"
                                className="mt-10 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02]"
                            >
                                <WhatsAppIcon className="h-5 w-5" />
                                Chat with a travel designer
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;
